import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import qs from "qs"
import Constants from 'expo-constants';
import { refreshAuthToken, isUnauthorizedResponse } from '../services/refreshToken';
import { EventBus } from '../services/EventBus';
import { API_VERSION } from '../constants/versionConstants';

const customBaseQuery = fetchBaseQuery({
    baseUrl: '',
    prepareHeaders: (headers, { getState }) => {
        const token = getState().data.token;
        headers.set('User-Agent', `YAROOMS-MEETING-ROOM-DISPLAY/${API_VERSION}`);

        if (token) {
            headers.set('authorization', `Bearer ${token}`)
        }

        return headers;
    },
});

export const dynamicBaseQuery = async (args, api, extraOptions) => {
    const serverUrl = api.getState().data.serverUrl;
    const adjustedServerUrl = serverUrl.endsWith('/') ? serverUrl : `${serverUrl}/`;
    args.url = serverUrl + args.url;

    const startTime = Date.now();

    // Emit API call event for RASP monitoring
    EventBus.emit('api_call', {
        url: args.url,
        method: args.method || 'GET',
        payload: args.body,
        headers: args.headers,
        timestamp: startTime
    });

    // Make the initial request
    let result = await customBaseQuery(args, api, extraOptions);

    // Check if we got a 401 Unauthorized response
    if (result.error && result.error.status === 401) {
        console.log('API_SLICE - 401 Unauthorized detected, attempting token refresh...');

        // Attempt to refresh the token
        const newToken = await refreshAuthToken();

        if (newToken) {
            console.log('API_SLICE - Token refreshed successfully, retrying request...');

            // Retry the original request with the new token
            result = await customBaseQuery(args, api, extraOptions);
        } else {
            console.error('API_SLICE - Token refresh failed, returning original error');
        }
    }

    // Also check for specific error response format {"success":0,"error":"Unauthorized"}
    if (result.data && result.data.success === 0 && result.data.error === "Unauthorized") {
        console.log('API_SLICE - Unauthorized error in response data, attempting token refresh...');

        // Attempt to refresh the token
        const newToken = await refreshAuthToken();

        if (newToken) {
            console.log('API_SLICE - Token refreshed successfully, retrying request...');

            // Retry the original request with the new token
            result = await customBaseQuery(args, api, extraOptions);
        } else {
            console.error('API_SLICE - Token refresh failed, returning original error');
        }
    }

    // Emit API response event for RASP monitoring
    const endTime = Date.now();
    EventBus.emit('api_call', {
        url: args.url,
        method: args.method || 'GET',
        payload: args.body,
        headers: args.headers,
        timestamp: startTime,
        responseTime: endTime - startTime,
        statusCode: result.error ? result.error.status : (result.data ? 200 : 500),
        success: !result.error
    });

    return result;
};


export const yaroomsApi = createApi({
    reducerPath: 'yaroomsApi',
    baseQuery: dynamicBaseQuery,
    endpoints: (builder) => ({
        post: builder.mutation({
            query: ({endpoint, data}) => {
                return ({
                    url: endpoint,
                    method: 'POST',
                    body: data ? qs.stringify(data) : null,
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded"
                    }
                });
            },
        }),
        delete: builder.mutation({
            query: ({endpoint, data}) => {
                return ({
                    url: endpoint,
                    method: 'DELETE',
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded"
                    }
                });
            },
        }),
        get: builder.mutation({
            query: ({endpoint, data}) => {
                return ({
                    url: endpoint,
                    method: 'GET',
                    params: data,
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    }
                });
            }
        }),
    }),
})

export const apiPost = (endpoint, data) => {
    return yaroomsApi.endpoints.post.initiate({
        endpoint: endpoint,
        data: data
    })
}

export const apiGet = (endpoint, data) => {
    return yaroomsApi.endpoints.get.initiate({
        endpoint: endpoint,
        data: data,
    })
}

export const apiDelete = (endpoint, data) => {
    return yaroomsApi.endpoints.delete.initiate({
        endpoint: endpoint,
        data: data,
    })
}

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
// export const { useAuth } = yaroomsApi